import { MAX_CANCEL_REASON_LENGTH } from '@repo/api-contracts';
import { type FormEvent, type ReactElement, useState } from 'react';
import { formatWon } from '@/shared/lib';
import { Button, ContentCard, FormField } from '@/shared/ui';

interface RefundFormProps {
  readonly balanceAmount: number;
  readonly isPending: boolean;
  /** 서버 거절 문구(토스 메시지 포함). */
  readonly error: string | null;
  readonly onSubmit: (input: { readonly amount: number; readonly reason: string }) => void;
}

/** 부분 환불 폼 — 금액 기본값은 남은 금액, 사유 필수 200자 이하(결제 설계 §12). */
export const RefundForm = ({
  balanceAmount,
  isPending,
  error,
  onSubmit,
}: RefundFormProps): ReactElement => {
  const [amount, setAmount] = useState(String(balanceAmount));
  const [reason, setReason] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const parsedAmount = Number(amount);

    if (!Number.isSafeInteger(parsedAmount) || parsedAmount <= 0) {
      setFieldError('환불 금액은 1원 이상의 정수로 입력해 주세요.');
      return;
    }

    if (parsedAmount > balanceAmount) {
      setFieldError(`환불 금액은 남은 금액(${formatWon(balanceAmount)}) 이하여야 합니다.`);
      return;
    }

    const trimmed = reason.trim();

    if (trimmed.length === 0 || trimmed.length > MAX_CANCEL_REASON_LENGTH) {
      setFieldError(`환불 사유는 1~${MAX_CANCEL_REASON_LENGTH}자로 입력해 주세요.`);
      return;
    }

    setFieldError(null);
    onSubmit({ amount: parsedAmount, reason: trimmed });
  };

  const message = fieldError ?? error;

  return (
    <ContentCard title="환불" className="mt-6">
      <form aria-label="환불" onSubmit={handleSubmit} className="flex flex-col gap-3">
        <FormField
          label="환불 금액(원)"
          inputMode="numeric"
          isRequired
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
        />
        <FormField
          label="환불 사유"
          isRequired
          maxLength={MAX_CANCEL_REASON_LENGTH}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
        {message ? (
          <p role="alert" className="text-sm text-destructive">
            {message}
          </p>
        ) : null}
        {/* 결제 상세에서 직접 한 환불은 고객 메일이 없다(주문 spec §6-5) — 운영자가 따로 안내해야 함을 알린다. */}
        <p className="text-sm text-muted-foreground">
          이 화면에서 처리한 환불 내역은 고객에게 자동으로 안내되지 않습니다. 제작 착수 후 취소 또는
          부분 환불을 진행한 경우, 고객에게 별도로 안내해 주세요.
        </p>
        <Button type="submit" variant="destructive" isLoading={isPending} className="self-start">
          환불하기
        </Button>
      </form>
    </ContentCard>
  );
};
